import { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Link } from "react-router-dom";
import { useSEO } from "../Utils/useSEO";
import HeroInteractiveBg from "../components/Three/HeroInteractiveBg";
import { motion, AnimatePresence } from "framer-motion";
import moduleSupplier from "../assets/images/module_supplier.png";
import moduleCompliance from "../assets/images/module_compliance.png";
import moduleContract from "../assets/images/module_contract.png";
import moduleRfq from "../assets/images/module_rfq.png";
import modulePo from "../assets/images/module_po.png";
import moduleDelivery from "../assets/images/module_delivery.png";
import yardSensorsRender from "../assets/images/yard_sensors_render.png";
import moduleGps from "../assets/images/module_gps.png";
import moduleDock from "../assets/images/module_dock.png";
import moduleException from "../assets/images/module_exception.png";
import moduleAp from "../assets/images/module_ap.png";
import moduleThreeway from "../assets/images/module_threeway.png";
import modulePayment from "../assets/images/module_payment.png";
import moduleScorecard from "../assets/images/module_scorecard.png";
import moduleAnalytics from "../assets/images/module_analytics.png";
import moduleCollaboration from "../assets/images/module_collaboration.png";
import moduleErp from "../assets/images/module_erp.png";
import moduleCopilot from "../assets/images/module_copilot.png";
import {
  UserPlus,
  ShieldCheck,
  FileCheck2,
  GitPullRequest,
  ClipboardList,
  Truck,
  Clock,
  MapPin,
  Layers,
  ShieldAlert,
  CreditCard,
  Sparkles,
  DollarSign,
  Award,
  BarChart3,
  MessageSquare,
  Database,
  Cpu,
  ArrowRight,
  Search,
  X
} from "lucide-react";

export default function FeaturesPage() {
  useSEO({
    title: "Features & Capabilities // Core Logistics Infrastructure",
    description: "Explore our automated RFQ management, JIT logistics, AI 3-way match, smart delivery tracking, and payment gateways."
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Modules" },
    { id: "compliance", label: "Onboarding & Compliance" },
    { id: "logistics", label: "Logistics & JIT" },
    { id: "finance", label: "Financials & AP" },
    { id: "ai", label: "AI & Integrations" },
  ];

  const modules = [
    {
      icon: <UserPlus className="w-6 h-6 text-brand-orange" />,
      title: "1. Supplier Onboarding",
      desc: "Self-service portal with custom risk profiles, digital checklist builders, and automated validation gates.",
      image: moduleSupplier,
      href: "/features/supplier-onboarding",
      category: "compliance",
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-brand-amber" />,
      title: "2. Compliance Verification",
      desc: "Continuous audit tracks, real-time background scans, ESG reporting, and certificate expiration alerts.",
      image: moduleCompliance,
      href: "/features/compliance-verification",
      category: "compliance",
    },
    {
      icon: <FileCheck2 className="w-6 h-6 text-brand-gold" />,
      title: "3. Contract Management",
      desc: "Digital secure repository with milestone triggers, SLA compliance registers, and automated renew alerts.",
      image: moduleContract,
      href: "/features/contract-management",
      category: "compliance",
    },
    {
      icon: <GitPullRequest className="w-6 h-6 text-brand-orange" />,
      title: "4. RFQ Management",
      desc: "Automated RFQ generation, secure bid portals, multi-dimensional response comparison, and award workflows.",
      image: moduleRfq,
      href: "/features/rfq-management",
      category: "logistics",
    },
    {
      icon: <ClipboardList className="w-6 h-6 text-brand-amber" />,
      title: "5. Purchase Orders",
      desc: "Instant digital purchase orders matching compliance rules, multi-level approvals, and supplier confirm receipts.",
      image: modulePo,
      href: "/features/purchase-orders",
      category: "logistics",
    },
    {
      icon: <Truck className="w-6 h-6 text-brand-gold" />,
      title: "6. Smart Delivery",
      desc: "Intelligent freight routing, carrier dispatch verification, automated transit logs, and custom gate checkouts.",
      image: moduleDelivery,
      href: "/features/smart-delivery",
      category: "logistics",
    },
    {
      icon: <Clock className="w-6 h-6 text-brand-orange" />,
      title: "7. JIT Logistics",
      desc: "Synchronized factory-to-dock logistics grids to minimize warehouse storage time and maintain JIT metrics.",
      image: yardSensorsRender,
      href: "/features/jit-logistics",
      category: "logistics",
    },
    {
      icon: <MapPin className="w-6 h-6 text-brand-amber" />,
      title: "8. GPS Tracking",
      desc: "Real-time vessel and cargo tracking with geofencing triggers and automated ETAs mapping terminal gates.",
      image: moduleGps,
      href: "/features/gps-tracking",
      category: "logistics",
    },
    {
      icon: <Layers className="w-6 h-6 text-brand-gold" />,
      title: "9. Dock Management",
      desc: "Dynamic yard slot reservations, check-in queuing, QR scanners, and slot congestion alerts.",
      image: moduleDock,
      href: "/features/dock-management",
      category: "logistics",
    },
    {
      icon: <ShieldAlert className="w-6 h-6 text-brand-orange" />,
      title: "10. Exception Management",
      desc: "AI diagnostics flagging delays, customs holds, or damage reports, with automated re-routing logic.",
      image: moduleException,
      href: "/features/exception-management",
      category: "logistics",
    },
    {
      icon: <CreditCard className="w-6 h-6 text-brand-amber" />,
      title: "11. AP Automation",
      desc: "Digital ledger invoice ingestion, compliance checks, tax validation, and centralized approval desks.",
      image: moduleAp,
      href: "/features/ap-automation",
      category: "finance",
    },
    {
      icon: <Sparkles className="w-6 h-6 text-brand-gold" />,
      title: "12. AI 3-Way Match",
      desc: "Autonomous cross-checking matching Purchase Orders, Delivery Notes, and Invoices with discrepancy detection.",
      image: moduleThreeway,
      href: "/features/ai-threeway-match",
      category: "finance",
    },
    {
      icon: <DollarSign className="w-6 h-6 text-brand-orange" />,
      title: "13. Payment Automation",
      desc: "Secure escrow releases, dynamic early payment discount schedules, and multi-currency global transfers.",
      image: modulePayment,
      href: "/features/payment-automation",
      category: "finance",
    },
    {
      icon: <Award className="w-6 h-6 text-brand-amber" />,
      title: "14. Supplier Scorecards",
      desc: "Multi-dimensional performance matrices evaluating delivery times, invoice accuracy, and compliance.",
      image: moduleScorecard,
      href: "/features/supplier-scorecards",
      category: "compliance",
    },
    {
      icon: <BarChart3 className="w-6 h-6 text-brand-gold" />,
      title: "15. Analytics",
      desc: "Centralized category spend dashboards, risk maps, bottlenecks analyzer, and automated auditor logs.",
      image: moduleAnalytics,
      href: "/features/analytics",
      category: "ai",
    },
    {
      icon: <MessageSquare className="w-6 h-6 text-brand-orange" />,
      title: "16. Communication Portal",
      desc: "Unified messaging thread with automatic translations, audit-trail locks, and shared document directories.",
      image: moduleCollaboration,
      href: "/features/communication-portal",
      category: "ai",
    },
    {
      icon: <Database className="w-6 h-6 text-brand-amber" />,
      title: "17. ERP Integration",
      desc: "Real-time sync adapters with SAP (S/4HANA), Oracle, Microsoft Dynamics 365, and major proprietary systems.",
      image: moduleErp,
      href: "/features/erp-integration",
      category: "ai",
    },
    {
      icon: <Cpu className="w-6 h-6 text-brand-gold" />,
      title: "18. AI Copilot",
      desc: "Conversational copilot (Aether Core) assisting with risk forecasting, PO approvals, and operational queries.",
      image: moduleCopilot,
      href: "/features/ai-copilot",
      category: "ai",
    },
  ];

  const filteredModules = modules.filter((mod) => {
    const matchesSearch =
      mod.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      mod.desc.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || mod.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 relative">
      {/* Page-wide Shimmer & Gradient Flow Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="gradient-flow-bg opacity-15 absolute inset-0" />
      </div>

      <Navbar />

      {/* Full-width Hero Section with 3D Background */}
      <section className="relative w-full pt-36 pb-20 overflow-hidden border-b border-brand-orange/10 bg-bg-secondary/20">
        {/* Interactive 3D Background */}
        <HeroInteractiveBg />

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-title tracking-wider animate-pulse">
            <Sparkles className="w-3.5 h-3.5" />
            <span>CAPABILITIES INDEX</span>
          </div>

          <h1 className="text-5xl md:text-[76px] font-title font-bold tracking-tight leading-none uppercase">
            Platform <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold">Modules</span>
          </h1>

          <p className="text-text-secondary text-base md:text-lg max-w-2xl leading-relaxed">
            Explore the 18 core structural components powering autonomous supplier operations, JIT logistics, accounts payable automation, and compliance tracking.
          </p>

          {/* Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-4 mt-2">
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              REQUEST DEMO
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-text-primary border border-text-muted/20 hover:border-brand-orange rounded-lg bg-transparent hover:text-brand-amber transition-all"
            >
              TALK TO AN EXPERT
            </Link>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 pb-24 relative z-10">

        {/* Sticky Search & Filter Section */}
        <div className="sticky top-20 z-40 -mx-6 px-6 py-4 bg-bg-primary/95 backdrop-blur-md border-b border-brand-orange/10 mb-12 shadow-lg shadow-black/10">
          <div className="glass-panel p-4 rounded-2xl border border-brand-orange/10 bg-bg-secondary/40 w-full flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search Input on Left */}
            <div className="relative flex-1 max-w-md">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted">
                <Search className="w-4 h-4" />
              </span>
              <input
                type="text"
                placeholder="Search modules (e.g. onboarding, AP, JIT)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-bg-primary/60 border border-brand-orange/20 focus:border-brand-orange/80 rounded-xl pl-10 pr-10 py-2.5 text-sm text-text-primary placeholder-text-secondary/50 focus:outline-none transition-colors"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Category Tabs on Right */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold tracking-wider transition-all uppercase cursor-pointer ${selectedCategory === cat.id
                      ? "bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-md shadow-brand-orange/15"
                      : "bg-bg-primary/40 text-text-secondary border border-text-muted/10 hover:border-brand-orange/30 hover:text-text-primary"
                    }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* 18 Modules Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative min-h-[300px]">
          <AnimatePresence mode="popLayout">
            {filteredModules.map((mod) => (
              <motion.div
                key={mod.title}
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                layout
                className="flex"
              >
                {(() => {
                  const CardInner = () => (
                    <>
                      {mod.image && (
                        <div className="w-full h-36 overflow-hidden relative">
                          <div className="absolute inset-0 bg-gradient-to-t from-bg-secondary via-transparent to-transparent z-10 opacity-70" />
                          <img
                            src={mod.image}
                            alt={mod.title}
                            className="w-full h-full object-cover transform scale-100 group-hover:scale-105 transition-all duration-700 ease-out filter brightness-[0.8] group-hover:brightness-100"
                          />
                        </div>
                      )}
                      <div className="p-6 flex flex-col gap-4 flex-1">
                        <div className="absolute top-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-orange to-brand-amber opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="w-10 h-10 rounded-xl bg-bg-primary flex items-center justify-center border border-brand-orange/10 group-hover:border-brand-orange/30 transition-colors">
                          {mod.icon}
                        </div>
                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <h3 className="font-title text-sm font-bold text-text-primary mb-2 group-hover:text-brand-orange transition-colors">
                              {mod.title}
                            </h3>
                            <p className="text-text-secondary text-xs leading-relaxed mb-4">{mod.desc}</p>
                          </div>
                          {mod.href && (
                            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-brand-orange uppercase tracking-wider group-hover:text-brand-amber transition-colors mt-4">
                              Explore Module
                              <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                          )}
                        </div>
                      </div>
                    </>
                  );

                  const cardClass = "glass-panel rounded-2xl flex flex-col border border-brand-orange/10 hover:border-brand-amber/35 hover:-translate-y-1 transition-all duration-300 relative group overflow-hidden w-full text-left";

                  return mod.href ? (
                    <Link to={mod.href} className={cardClass}>
                      <CardInner />
                    </Link>
                  ) : (
                    <div className={cardClass}>
                      <CardInner />
                    </div>
                  );
                })()}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {filteredModules.length === 0 && (
          <div className="text-center py-16 glass-panel rounded-3xl border border-brand-orange/10 max-w-md mx-auto mt-8">
            <p className="text-text-secondary text-sm">No modules match your search query or selected category.</p>
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("all");
              }}
              className="mt-4 px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-amber text-white text-xs font-semibold rounded-lg hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Custom enterprise workflow promo panel */}
        <div className="mt-20">
          <div className="glass-panel p-10 md:p-12 rounded-3xl border border-brand-orange/15 bg-gradient-to-r from-bg-secondary/40 to-bg-primary relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl text-left">
              <h2 className="text-xl md:text-2xl font-title font-bold mb-3">Need a custom enterprise workflow?</h2>
              <p className="text-text-secondary text-xs md:text-sm leading-relaxed">
                Connect our API directly with proprietary ledger systems, custom shipping portals, or private databases. Our solutions engineers can build a tailored pipeline for your network.
              </p>
            </div>
            <Link
              to="/contact"
              className="px-6 py-3.5 font-semibold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-md shadow-brand-orange/10 hover:shadow-brand-amber/25 hover:scale-[1.02] active:scale-[0.98] transition-all whitespace-nowrap"
            >
              REQUEST SOLUTIONS SESSION
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
